import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid2, Typography } from '@mui/material';
import Footer from 'components/FooterComponent';
import CardBigNumbers from './_components/CardBigNumbers';
import TableComponent, { IColumnProps } from 'components/TableComponent';
import personaDash from '../../assets/images/svgs/DashboardSVGS/RoboticsDashboard.svg';
import {
  EVENT_ENDPOINT,
  getStreamByEndpointAsync,
} from 'services/Events';
import { transformDateStringInDate } from 'utils/TransformUtils';
import CardLastEvent from './_components/CardLastEventDashboard';
import CameraIcon from '@mui/icons-material/Camera';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import BarChartWeak from './_components/_ui/BarChartWeak';
import BarChartYear from './_components/_ui/BarChartMonth';
import BarChartDay from './_components/_ui/BarChartDay';
import PieChartLastEventsType from './_components/_ui/PieChartLastEventsType';
import { IPaginationParams } from 'services/types';
import HeaderDashboard from './_components/_ui/Header';
import BigNumber from 'components/BigNumber';
import { getEmail, getUserName } from 'services/DefaultService';
import { AppContext } from 'context/AppContextProvider';
import { Permissions } from 'services/enums';
import { useTranslation } from 'react-i18next';
import { getDashboardSummaryAsync } from 'services/Dashboard';

interface Camera {
  Status: string;
}

interface ChartPoint {
  label: string;
  value: number;
}

interface DashboardData {
  counters: {
    devices: number;
    activeDevices: number;
    students: number;
    faces: number;
    responsibles: number;
  };
  events: {
    total: number;
    lpr: number;
    face: number;
    ivs: number;
    temp: number;
  };
  charts: {
    currentWeek: ChartPoint[];
    currentMonth: ChartPoint[];
    currentYear: ChartPoint[];
  }
}

const DashboardPageClient: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isRouteAllowed } = useContext(AppContext);

  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 7,
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [dashData, setDashData] = useState<DashboardData | null>(null);
  const [user, setUser] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [lastEvent, setLastEvent] = useState();

  const hasStudentPermission = isRouteAllowed(Permissions.STUDENT);

  // Buscar dados (useEffects)
  // Lógica para obter o último evento
  useEffect(() => {
    if (events.length > 0) {
      const mostRecentEvent = events.reduce((latest, current) =>
        transformDateStringInDate(current.createdAt) >
          transformDateStringInDate(latest.createdAt)
          ? current
          : latest,
      );
      setLastEvent(mostRecentEvent);
    }
  }, [events]);

  useEffect(() => {
    getStreamByEndpointAsync(EVENT_ENDPOINT, setEvents, pagination);
  }, []);

  useEffect(() => {
    const userName = getUserName();
    if (typeof userName === 'string') {
      setUser(userName);
    } else if (Array.isArray(userName)) {
      setUser(userName.join(', '));
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const email = getEmail();
    if (typeof email === 'string') {
      setEmail(email);
    } else if (Array.isArray(email)) {
      setEmail(email.join(', '));
    } else {
      setEmail(null);
    }
  }, []);

  useEffect(() => {
    getDashboardSummaryAsync().then(data => setDashData(data));
  }, []);

  const columns: IColumnProps[] = [
    { id: 'description', label: 'Descrição', width: '30%' },
    { id: 'device.name', label: 'Dispositivo', width: '20%' },
  ];

  return (
    <Box
      height={'100%'}
      width={'100%'}
      flexDirection="row-reverse"
      sx={{
        '@media (max-width: 900px)': {
          width: '100%',
          paddingX: 2,
        },
      }}
    >
      <HeaderDashboard></HeaderDashboard>
      <Box>
        <Grid2
          container
          display={'flex'}
          flexDirection={'row'}
          justifyContent="center"
          alignItems="center"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            },
          }}
        >
          <Grid2
            container
            width={'69%'}
            height={400}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              background:
                'linear-gradient(to top left, rgb(34, 53, 100), rgb(35, 43, 63), #202940)',
              borderRadius: 2,
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              color: '#fff',
              padding: 3,
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                width: '100%',
                height: 'auto',
                padding: 2,
                marginBottom: 2,
                marginTop: 3,
              },
            }}
          >
            <Box
              width={'60%'}
              sx={{
                textAlign: 'left',
                padding: 2,
                justifyContent: 'flex-start',
                '@media (max-width: 900px)': {
                  width: '100%',
                  padding: 1,
                  marginTop: 3,
                },
              }}
            >
              <Box>
                <h2 style={{ fontSize: '2rem', marginBottom: '-4rem' }}>
                  {t('dashboard.welcome')}
                </h2>
                <h2
                  style={{
                    fontSize: '3.5rem',
                    marginBottom: '-1rem',
                    wordBreak: 'break-word',
                  }}
                >
                  {user}
                </h2>
                <p style={{ fontSize: '1rem', maxWidth: '100%' }}>
                  {t('dashboard.description')}
                </p>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  maxWidth: '60%',
                  justifyContent: 'space-between',
                  '@media (max-width: 900px)': {
                    maxWidth: '100%',
                    flexDirection: 'row',
                    gap: 2,
                  },
                }}
              >
                <BigNumber
                  value={dashData?.counters.devices ?? 0}
                  text={t('common.devices')}
                />
                <BigNumber
                  value={dashData?.events.total ?? 0}
                  text={t('common.events')}
                />
              </Box>
            </Box>

            <Box
              width={'40%'}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '@media (max-width: 900px)': {
                  width: '100%',
                  marginTop: 2,
                },
              }}
            >
              <img
                src={personaDash}
                alt="Painel ilustrativo"
                style={{
                  maxWidth: '75%',
                  height: 'auto',
                  borderRadius: '1rem',
                }}
              />
            </Box>
          </Grid2>

          <Grid2
            container
            width={'30%'}
            height={400}
            sx={{
              marginLeft: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, rgb(34, 53, 100) 0%, rgb(35, 43, 63) 60%, #202940 100%)',
              borderRadius: 2,
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              padding: 0,
              '@media (max-width: 900px)': {
                width: '100%',
                marginLeft: 0,
                height: 'auto',
                marginTop: 2,
              },
            }}
          >
            <Box
              width="100%"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: 4,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.01)',
                boxShadow: 'none',
                height: '100%',
                justifyContent: 'center',
                '@media (max-width: 900px)': {
                  padding: 2,
                  marginTop: 3,
                },
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                color="#fff"
                gutterBottom
              >
                {t('dashboard.accountSummary')}
              </Typography>
              <Typography variant="body2" color="#b0b3bb" gutterBottom>
                {t('common.client')}: {user || 'Nome do Cliente'}
              </Typography>
              <Typography variant="body2" color="#b0b3bb" gutterBottom>
                {t('Email')}: {email || 'E-mail do Cliente'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  width: '100%',
                  mt: 1,
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" color="#b0b3bb">
                    {t('common.devices')}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="#fff"
                    fontWeight="bold"
                  >
                    {dashData?.counters.devices ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2
          container
          spacing={1}
          display={'flex'}
          width={'100%'}
          justifyContent="center"
          alignItems="center"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
              width: '100%',
              marginTop: 3,
              gap: 2,
            },
          }}
        >
          <Grid2
            width={'25%'}
            sx={{
              '@media (max-width: 900px)': {
                width: '100%',
                marginBottom: 2,
              },
            }}
          >
            <Box
              height={'30vh'}
              sx={{ '@media (max-width: 600px)': { height: 'auto' } }}
            >
              <CardBigNumbers
                title={t('dashboard.activeDevices')}
                icone={<CameraIcon />}
                data={dashData?.counters.activeDevices ?? 0}
              />
            </Box>
          </Grid2>
          <Grid2
            width={'25%'}
            sx={{
              '@media (max-width: 900px)': {
                width: '100%',
                marginBottom: 2,
              },
            }}
          >
            <Box
              height={'30vh'}
              sx={{ '@media (max-width: 600px)': { height: 'auto' } }}
            >
              <CardBigNumbers
                title={t('dashboard.registeredDevices')}
                icone={<AddAPhotoIcon />}
                data={dashData?.counters.devices ?? 0}
              />
            </Box>
          </Grid2>
          <Grid2
            width={'24%'}
            sx={{
              '@media (max-width: 900px)': {
                width: '100%',
                marginBottom: 2,
              },
            }}
          >
            <Box
              height={'30vh'}
              sx={{ '@media (max-width: 600px)': { height: 'auto' } }}
            >
              <CardBigNumbers
                title={t('dashboard.faces')}
                icone={<EmojiEmotionsIcon />}
                data={dashData?.counters.faces ?? 0}
              />
            </Box>
          </Grid2>
          <Grid2
            width={'24%'}
            justifyContent={'center'}
            alignContent={'center'}
            sx={{
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                width: '100%',
                marginBottom: 2,
              },
            }}
          >
            {hasStudentPermission ? (
              <>
                <Box
                  height={'14.5vh'}
                  marginY={1}
                  sx={{
                    '@media (max-width: 900px)': {
                      height: 'auto',
                      marginY: 1,
                      width: '100%',
                    },
                  }}
                >
                  <CardBigNumbers
                    title={t('common.students')}
                    icone={<SchoolIcon />}
                    data={dashData?.counters.students ?? 0}
                    position="row-reverse"
                    margin={2}
                  />
                </Box>
                <Box
                  height={'14.5vh'}
                  marginY={1}
                  sx={{
                    '@media (max-width: 900px)': {
                      height: 'auto',
                      marginY: 1,
                      width: '100%',
                    },
                  }}
                >
                  <CardBigNumbers
                    title={t('common.parents')}
                    data={dashData?.counters.responsibles ?? 0}
                    icone={<FamilyRestroomIcon />}
                    position="row-reverse"
                    margin={2}
                  />
                </Box>
              </>
            ) : (
              <Box
                height={'30vh'}
                marginY={1}
                sx={{
                  '@media (max-width: 900px)': {
                    height: 'auto',
                    marginY: 1,
                    width: '80%',
                  },
                }}
              >
                <CardBigNumbers
                  title={t('common.registeredEvents')}
                  icone={<CameraIcon />}
                  data={dashData?.events.total ?? 0}
                  margin={2}
                />
              </Box>
            )}
          </Grid2>
        </Grid2>
        <Grid2
          container
          display={'flex'}
          justifyContent="center"
          alignItems="center"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            },
          }}
        >
          <Grid2
            display={'flex'}
            justifyContent="space-around"
            alignItems="center"
            width={'100%'}
            sx={{
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              },
            }}
          >
            <Box
              mb={3}
              width={'64%'}
              height={'50vh'}
              sx={{
                '@media (max-width: 900px)': {
                  width: '100%',
                  height: '45vh',
                },
                '@media (max-width: 600px)': {
                  height: '30vh',
                },
              }}
            >
              <BarChartDay data={dashData?.charts.currentMonth} />
            </Box>
            {window.innerWidth > 900 && (
              <Box
                mb={3}
                width={'35%'}
                height={'50vh'}
                sx={{
                  '@media (max-width: 900px)': {
                    display: 'none',
                  },
                }}
              >
                <PieChartLastEventsType events={dashData?.events} />
              </Box>
            )}
          </Grid2>
        </Grid2>
        <Grid2
          container
          spacing={10}
          display={'flex'}
          justifyContent="center"
          alignItems="center"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            },
          }}
        >
          <Grid2
            spacing={5}
            display={'flex'}
            justifyContent="space-around"
            alignItems="center"
            width={'100%'}
            sx={{
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              },
            }}
          >
            <Box
              mb={3}
              width={'34%'}
              height={'50vh'}
              sx={{
                '@media (max-width: 900px)': {
                  flexDirection: 'column',
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                  height: 'auto',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#202940',
                  width: '100%',
                  height: '100%',
                  maxHeight: '50vh',
                  borderRadius: 4,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                  '@media (max-width: 900px)': {
                    width: '100%',
                    height: 'auto',
                  },
                }}
              >
                <TableComponent
                  rows={events}
                  sortByName={'createdAt'}
                  columns={columns}
                  totalCount={totalCount}
                  onChangePageOrRowsPerPage={(param) => {
                    setPagination(param);
                  }}
                  size={'small'}
                  height={'50vh'}
                  width={'100%'}
                />
              </Box>
            </Box>
            <Box
              mb={3}
              width={'65%'}
              height={'50vh'}
              sx={{
                '@media (max-width: 900px)': {
                  flexDirection: 'column',
                  width: '100%',
                  height: '30vh',
                },
                '@media (max-width: 600px)': {
                  height: 'auto',
                },
              }}
            >
              <BarChartWeak data={dashData?.charts.currentWeek} />
            </Box>
          </Grid2>
        </Grid2>
        <Grid2
          container
          spacing={10}
          display={'flex'}
          justifyContent="center"
          alignItems="center"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            },
          }}
        >
          <Grid2
            spacing={5}
            display={'flex'}
            justifyContent="space-around"
            alignItems="center"
            width={'100%'}
            sx={{
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              },
            }}
          >
            <Box
              mb={3}
              width={'64%'}
              height={'50vh'}
              sx={{
                '@media (max-width: 900px)': {
                  flexDirection: 'column',
                  width: '100%',
                  height: '30vh',
                },
                '@media (max-width: 600px)': {
                  height: 'auto',
                },
              }}
            >
              <BarChartYear data={dashData?.charts.currentYear} />
            </Box>
            <Box
              mb={3}
              width={'35%'}
              height={'50vh'}
              sx={{
                '@media (max-width: 900px)': { width: '100%', height: 'auto' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  '@media (max-width: 900px)': {
                    width: '100%',
                    height: 'auto',
                  },
                }}
              >
                <CardLastEvent
                  event={lastEvent}
                  title={'Ultimo evento'}
                  width="35vh"
                />
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Footer />
    </Box>
  );
};

export default DashboardPageClient;
